import * as utils from '../utils.js';
import * as http from '../../utils/http.js';


/**
 * TIPS:
 *
 * upload & download with progress
 * - https://stackoverflow.com/questions/35711724/upload-progress-indicators-for-fetch
 *
 */

// source: https://chatgpt.com/c/69177b05-80dc-8328-8f06-38a664fd4b43

export class RemoteService {

    /**
     * @param {Object} config
     * @param {string} [config.baseUrl]        - Volitelný base URL (např. '/api')
     * @param {Object} [config.defaultHeaders] - Výchozí hlavičky pro všechna volání
     * @param {'json'|'text'|'blob'|'auto'} [defaultContentType]
     * @param {'json'|'text'|'blob'|'auto'} [defaultResponseType]
     * @param {'strict'|'lenient'} [defaultResponseTypeMode]
     * @param {'cors' | 'same-origin' | 'no-cors' } [defaultCorsPolicy]
     * @param {(status:number) => boolean} [defaultResponseValidator]
     * @param {AuthDef} [defaultAuth]           - Výchozí autentizace
     * @param {number} [config.timeout]        - Výchozí timeout v ms (např. 15000)
     * @param {Object} [config.authManager]    - Volitelný správce autentizace (bearer, cookies)
     */
    constructor(config = {}) {

        const defaultValidator = (status) => (status >= 200 && status < 300) || status == 302;

        const {
            baseUrl = '',
            defaultHeaders = {},
            defaultContentType = 'auto',
            defaultResponseType = 'auto',
            defaultResponseTypeMode = 'lenient',
            defaultCorsPolicy = 'same-origin',
            defaultResponseValidator = defaultValidator,
            defaultAuth,
            timeout = 15000,
            authManager = null
        } = config;


        this.baseUrl = baseUrl.replace(/\/+$/, ''); // odstraní trailing slash
        this.defaultHeaders = { ...defaultHeaders };
        this.defaultContentType = defaultContentType;
        this.defaultResponseType = defaultResponseType;
        this.defaultResponseTypeMode = defaultResponseTypeMode;
        this.defaultCorsPolicy = defaultCorsPolicy;
        this.defaultResponseValidator = defaultResponseValidator ?? defaultValidator;
        this.defaultAuth = defaultAuth;
        this.timeout = timeout;

        /** @type {any|null} */
        this.authManager = authManager;

    }


    /**
     * Přidá / přepíše defaultní hlavičky.
     */
    setDefaultHeader(name, value) {

        if (value == null) {
            delete this.defaultHeaders[name];
        } else {
            this.defaultHeaders[name] = value;
        }

    }

    // ----------------------------------------------------------

    /**
     * @typedef {Object} CallOptions
     * @property {Object<string,string>} [headers]
     * @property {'json'|'text'|'blob'|'auto'} [contentType]
     * @property {'json'|'text'|'blob'|'auto'} [responseType]
     * @property {'strict'|'lenient'} [responseTypeMode]
     * @property {'cors' | 'same-origin' | 'no-cors' } [corsPolicy]
     * @property {(status:number) => boolean} [responseValidator]
     * @property {{
     *   type:'bearer',
     *   token?:string,
     *   manager?: {
     *     refreshToken: () => Promise<void>,
     *     getAccessToken: () => string | null
     *   }
     * } | {
     *   type:'basic',
     *   username:string,
     *   password:string
     * } | {
     *   type:'cookie',
     *   manager?: {
     *     refreshToken: () => Promise<void>,
     *     // cookies většinou žádný token nepotřebují
     *   }
     * }} [auth]
     * @property {boolean} [skipAuthRefresh]
     * @property {number} [timeout]
     */

    /**
     * @typedef {"GET" | "POST" | "PUT" | "DELETE"} HttpMethod
     */

    /**
     * @param {HttpMethod} method - Metoda volání.
     * @param {string} url - Adresa volání.
     * @param {*} data - Data volání.
     * @param {CallOptions} options={}
     */
    async call(method, url, data = null, options = {}) {

        const request = this._prepareRequest(method, url, data, options);
        const httpResponse = await this._executeCall(request);
        const response = await this._parseResponse(request, httpResponse);

        if (!response.ok)
            throw response;

        return response;

    }

    _prepareRequest(method, url, data = null, options = {}) {

        let {
            headers,
            contentType,
            responseType,
            responseTypeMode,
            responseValidator,
            corsPolicy,
            timeout,
            auth,
            skipAuthRefresh = false,
        } = options;

        contentType ??= this.defaultContentType;
        responseType ??= this.defaultResponseType;
        responseTypeMode ??= this.defaultResponseTypeMode;
        responseValidator = (responseValidator && typeof responseValidator === 'function') ? responseValidator : this.defaultResponseValidator;

        timeout ??= this.timeout;

        corsPolicy ??= this.defaultCorsPolicy;

        // auth z options, nebo výchozí z instance (bearer s AuthManagerem)
        let effectiveAuth = auth || null;

        if (!effectiveAuth) {
            if (this.authManager) {
                if (this.authManager.isCookieMode()) {
                    effectiveAuth = {
                        type: 'cookie',
                        manager: this.authManager
                    };
                } else {
                    effectiveAuth = {
                        type: 'bearer',
                        manager: this.authManager
                    };
                }
            } else if (this.defaultAuth) {
                effectiveAuth = this.defaultAuth;
            }
        }

        let effectiveUrl = url;

        if (this.baseUrl && !/^https?:\/\//i.test(url)) {
            if (url != '' && !url.startsWith('/')) {
                url = '/' + url;
            }
            effectiveUrl = this.baseUrl + url;
        }

        const request = {
            url: effectiveUrl,
            init: {
                method: method?.toUpperCase() ?? 'GET',
                headers: {
                    ...this.defaultHeaders,
                    ...(headers || {}),
                },
                mode: corsPolicy
            },
            timeout,
            responseType,
            responseTypeMode,
            responseValidator,
            auth: effectiveAuth,
            skipAuthRefresh,
            _retryAfterRefresh: false, // interní flag pro 401→refresh→retry
        };

        // Content-Type pro request body
        switch (contentType) {
            case 'json':
                request.init.headers['Content-Type'] = 'application/json; charset=utf-8';
                break;
            case 'text':
                request.init.headers['Content-Type'] = 'text/*';
                break;
        }

        // Accept hint pro server
        switch (responseType) {
            case 'json':
                request.init.headers['Accept'] = 'application/json';
                break;
            case 'text':
                request.init.headers['Accept'] = 'text/*';
                break;
        }

        if (effectiveAuth) {

            switch (effectiveAuth.type) {

                case 'bearer': {
                    const tokenFromOptions = effectiveAuth.token;
                    const tokenFromManager = effectiveAuth.manager?.getAccessToken?.();
                    const bearerToken = tokenFromOptions ?? tokenFromManager ?? '';

                    if (bearerToken) {
                        request.init.headers['Authorization'] = 'Bearer ' + bearerToken;
                        request.init.credentials = 'same-origin';
                    }

                    break;
                }

                case 'cookie':
                    // cookies-based auth – typicky rely na HttpOnly cookie
                    request.init.credentials = 'include';
                    break;

                case 'basic': {
                    const username = effectiveAuth.username || '';
                    const password = effectiveAuth.password || '';
                    const basicToken = btoa(`${username}:${password}`);
                    request.init.headers['Authorization'] = `Basic ${basicToken}`;
                    break;
                }

            }

        }

        if (data != null) {

            if (request.init.method === 'GET' || request.init.method === 'HEAD') {

                let query = null;
                if (utils.isObject(data)) {
                    query = http.encodeQueryString(data);
                } else if (utils.isString(data)) {
                    query = data;
                }

                if (query) {
                    const separator = effectiveUrl.includes('?') ? '&' : '?';
                    effectiveUrl = `${effectiveUrl}${separator}${query}`;
                    request.url = effectiveUrl;
                }

            } else {
                if (contentType === 'json') {
                    request.init.body = JSON.stringify(data);
                } else if (data !== null && data !== undefined) {
                    request.init.body = data;
                }
            }

        }

        return request;

    }

    async _executeCall(request) {

        const controller = new AbortController();
        request.init.signal = controller.signal;

        const timeoutId = setTimeout(() => controller.abort(), request.timeout);

        const doFetch = async () => { return fetch(request.url, request.init); };

        try {

            let httpResponse = await doFetch();

            // Automatický refresh pro 401
            if (
                httpResponse.status === 401 &&
                !request.skipAuthRefresh &&
                request.auth &&
                (request.auth.type === 'bearer' || request.auth.type === 'cookie') &&
                request.auth.manager &&
                !request._retryAfterRefresh
            ) {

                const manager = request.auth.manager;

                // pokus o refresh tokenů
                await manager.refresh();

                if (request.auth.type === 'bearer') {

                    const newToken = manager.getAccessToken?.();
                    if (!newToken) {
                        // žádný token → nemá smysl retry, necháme 401 být
                        return httpResponse;
                    }

                    request.init.headers['Authorization'] = 'Bearer ' + newToken;

                }

                // pro cookies žádný token nepotřebujeme – cookies se aktualizují samy
                request._retryAfterRefresh = true;

                httpResponse = await doFetch();

            }

            return httpResponse;

        } catch (err) {

            clearTimeout(timeoutId);
            throw err;

        } finally {

            clearTimeout(timeoutId);

        }

    }

    async _parseResponse(request, httpResponse) {

        let response = {
            ok: request.responseValidator(httpResponse.status),
            status: httpResponse.status,
            statusText: httpResponse.statusText,
            httpResponse: httpResponse
        };

        if (httpResponse.bodyUsed)
            return response;

        // typické „prázdné“ odpovědi
        if (httpResponse.status === 204 || httpResponse.status === 304) {
            response.payloadType = 'empty';
            response.payload = null;
            return response;
        }

        if (httpResponse.headers.has('Content-Length')) {
            let payloadLength = parseInt(httpResponse.headers.get('Content-Length'));
            if (isNaN(payloadLength))
                payloadLength = 0;
            if (!payloadLength) {
                response.payloadType = 'empty';
                response.payload = null;
                return response;
            }
        }

        const hint = request.responseType || 'auto';
        const mode = request.responseTypeMode || 'lenient';

        let httpContentType = httpResponse.headers.has('Content-Type')
            ? httpResponse.headers.get('Content-Type')
            : 'text/plain';

        const ctype = httpContentType.toLowerCase();

        let payloadType;

        if (hint !== 'auto') {
            // klient explicitně řekl, co chce
            payloadType = hint;
        } else {
            // AUTO mód – podle Content-Type
            if (ctype.includes('application/json')) {
                payloadType = 'json';
            } else if (ctype.includes('text/xml') || ctype.includes('application/xml')) {
                payloadType = 'xml';
            } else if (ctype.includes('text')) {
                payloadType = 'text';
            } else if (ctype.includes('image') || ctype.includes('video') || ctype.includes('application/octet-stream')) {
                payloadType = 'blob';
            } else if (ctype.includes('multipart/form-data')) {
                payloadType = 'form';
            } else {
                payloadType = 'buffer';
            }
        }

        response.payloadType = payloadType;

        switch (payloadType) {

            case 'text': {
                response.payload = await httpResponse.text();
                break;
            }

            case 'json': {
                const raw = await httpResponse.text();
                try {
                    response.payload = raw ? JSON.parse(raw) : null;
                } catch (err) {

                    if (mode === 'strict') {
                        // v strict režimu chyba = výjimka
                        err.response = response;
                        err.raw = raw;
                        throw err;
                    }

                    // lenient: zachovat maximum informací
                    response.parseError = err;
                    response.payloadRaw = raw;
                    response.payloadType = 'text';
                    response.payload = raw;
                }
                break;
            }

            case 'xml': {
                const domtext = await httpResponse.text();
                try {
                    response.payload = new window.DOMParser().parseFromString(domtext, 'application/xml');
                } catch (err) {

                    if (mode === 'strict') {
                        err.response = response;
                        err.raw = domtext;
                        throw err;
                    }

                    response.parseError = err;
                    response.payloadRaw = domtext;
                    response.payloadType = 'text';
                    response.payload = domtext;
                }
                break;
            }

            case 'blob': {
                response.payload = await httpResponse.blob();
                break;
            }

            case 'form': {
                response.payload = await httpResponse.formData();
                break;
            }

            default: {
                // buffer / neznámý typ
                response.payload = await httpResponse.arrayBuffer();
                break;
            }

        }

        return response;

    }

    // --------------------------------------------------------------------------------------------

    async get(url, data, options) {

        return this.call('GET', url, data, options);

    };

    async put(url, data, options) {

        return this.call('PUT', url, data, options);

    };

    async post(url, data, options) {

        return this.call('POST', url, data, options);

    };

    async delete(url, data, options) {

        return this.call('DELETE', url, data, options);

    };

}
